import type { UserRole } from '@/types/domain';

export const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Admin',
  MANAGER:     'Manager',
  OFFICE_TEAM: 'Team',
  SITE_TEAM:   'Team',
};

/** Badge colours per role. Single source of truth — do not re-declare per page. */
export const ROLE_COLORS: Record<UserRole, { bg: string; text: string }> = {
  SUPER_ADMIN: { bg: '#0a0a0a', text: '#ffffff' },
  MANAGER:     { bg: '#eef2ff', text: '#3730a3' },
  OFFICE_TEAM: { bg: '#f0fdf4', text: '#15803d' },
  SITE_TEAM:   { bg: '#f0fdf4', text: '#15803d' },
};

export const ROLE_FALLBACK_COLOR = { bg: '#f9fafb', text: '#374151' };

/** Look up a role's label from an unvalidated string (API rows are typed loosely). */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role as UserRole] ?? role;
}

export function roleColor(role: string): { bg: string; text: string } {
  return ROLE_COLORS[role as UserRole] ?? ROLE_FALLBACK_COLOR;
}

/** Only three roles exposed in the UI: Admin, Manager, Team.
 *  Both OFFICE_TEAM and SITE_TEAM display as "Team" — we use SITE_TEAM as the
 *  canonical value for new "Team" users; OFFICE_TEAM is treated identically if it
 *  appears in the database from older records. */
export const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'SUPER_ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'SITE_TEAM', label: 'Team' },
];

/** Roles an actor may assign. Only an Admin can create other Admins. */
export function assignableRoles(isAdmin: boolean) {
  if (isAdmin) return ROLE_OPTIONS;
  return ROLE_OPTIONS.filter((o) => o.value !== 'SUPER_ADMIN');
}

export function roleTone(role: UserRole): 'black' | 'blue' | 'green' | 'gray' {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'black';
    case 'MANAGER':
      return 'blue';
    case 'OFFICE_TEAM':
    case 'SITE_TEAM':
      return 'green';
    default:
      return 'gray';
  }
}

/**
 * Roles that work inside one project at a time.
 *
 * Site Agent Users and Drawing Validation Users work within project scope;
 * Admin and Project Manager work across the whole organisation.
 */
export const PROJECT_SCOPED_ROLES = new Set<UserRole>(['OFFICE_TEAM', 'SITE_TEAM']);

export function isProjectScoped(role: string | undefined): boolean {
  return !!role && PROJECT_SCOPED_ROLES.has(role as UserRole);
}
